import { useState, useEffect } from 'react';
import { useActivePlayer } from '@/context/ActivePlayerContext';
import { useBackgroundSync } from '@/context/BackgroundSyncContext';
import clubStyles from '../Club.module.scss';
import { createDeletedTourBag } from '@/utils';
import wedgeStyles from './Wedge.module.scss';
import Edit from './Edit';
import { isFieldVisible } from '@/library/FieldVisibilityHelper';
import { PageReadability } from '@/library/TourPageSettingsHelper';
import { headTypeMap, Action } from '@/constants';
import { TourBagDetail, TourBagMstrPayload } from '@/api/models';
import { TourBagDetailAddNew } from '@/db/models';
import { useToast } from '@/context/ToastContext';
import { TourBagService } from '@/service';
import DeletedHistory from '../DeletedHistory';
import SpinnerOverlay from '@/common/SpinnerOverlay';
import useOnlineStatus from '@/hooks/useOnlineStatus';
import { Fragment } from 'react/jsx-runtime';
import FinalHeadWeightModal from './FinalHeadWeightModal';
import { Accordion, ControlledAccordion } from '@/common/Accordion';
import InTheBag from '@/common/InTheBag';
import Tile from '@/common/Tile';
import TextAreaDisplay from '@/common/TextArea/Display';
import { calculateFinalHeadWeight } from '../Iron/caluclateFinalHeadWeight';
import { normalizeString } from '@/utils';
import ClubHistory from '../ClubHistory/ClubHistory';
import OfflineModal from '@/common/OfflineModal';
import Linkify from 'linkify-react';
import { WedgePhotoCaptureSection } from '@/common/PhotoCaptureSection';
interface IRenderWedgeProps {
  wedge: TourBagDetail;
  pageReadability: PageReadability | undefined;
  /* eslint-disable no-unused-vars */
  onFinalHWClick?: (wedge: TourBagDetail) => void;
  /* eslint-enable no-unused-vars */
}

const renderWedge = ({ wedge, pageReadability, onFinalHWClick }: IRenderWedgeProps) => {
  const finalHeadWeight = calculateFinalHeadWeight({
    headWeight: wedge.headWeight || 0,
    drill: wedge.drill,
    tipPlug: wedge.tipPlug,
    glueWeight: wedge.glueWeight,
    leadTape: wedge.leadTape,
  });

  const module = 'Wedge';

  return (
    <>
      <div className={clubStyles.tileRow}>
        {isFieldVisible(module, 'hand', pageReadability) && <Tile label="Hand" value={normalizeString(wedge.hand)} />}
        {isFieldVisible(module, 'headFinishName', pageReadability) && <Tile label="Finish" value={normalizeString(wedge.headFinishName)} variant="large" />}
        {isFieldVisible(module, 'headFinishNote', pageReadability) && <Tile label="Finish Note" value={normalizeString(wedge.headFinishNote)} variant="extra" />}
        {isFieldVisible(module, 'familyName', pageReadability) && <Tile label="Family" value={normalizeString(wedge.familyName)} variant="large" />}
        {isFieldVisible(module, 'familyNote', pageReadability) && <Tile label="Family Note" value={normalizeString(wedge.familyNote)} variant="extra" />}
        {isFieldVisible(module, 'swingWt', pageReadability) && <Tile label="Swing Wt." value={normalizeString(wedge.swingWtPrefixName + ' ' + wedge.swingWtSuffixName)} variant="large" />}
        {isFieldVisible(module, 'finalHW', pageReadability) && (
          <>
            {onFinalHWClick && (
              <span className={clubStyles.finalHWWrapper} role="button" onClick={() => onFinalHWClick(wedge)}>
                <Tile label="Final HW" value={normalizeString(finalHeadWeight?.toString())} tileStyle={clubStyles.finalHW} />
              </span>
            )}
            {!onFinalHWClick && <Tile label="Final HW" value={normalizeString(finalHeadWeight?.toString())} tileStyle={clubStyles.finalHW} />}
          </>
        )}
        {isFieldVisible(module, 'loft', pageReadability) && <Tile label="Loft" value={normalizeString(wedge.loftDigit + wedge.loftFraction)} variant="large" />}
        {isFieldVisible(module, 'lie', pageReadability) && <Tile label="Lie" value={normalizeString(wedge.lieDigit + wedge.lieFraction)} variant="large" />}
        {isFieldVisible(module, 'stamping', pageReadability) && <TextAreaDisplay label="Stamping" value={normalizeString(wedge.stamping)} variant="halfSize" />}
      </div>
      {isFieldVisible(module, 'grindDetails', pageReadability) && (
        <div className={clubStyles.tileRow}>
          <div>
            <div className={clubStyles.categoryName}>Grind Details</div>
            <div>{normalizeString(wedge.grindDetails)}</div>
          </div>
        </div>
      )}
      {isFieldVisible(module, 'shaftName', pageReadability) && (
        <>
          <div className={clubStyles.tileRow}>
            <div className={clubStyles.category}>
              <div className={clubStyles.categoryName}>Shaft</div>
              <div className={clubStyles.categoryTitle}>{wedge.shaftName}</div>
            </div>
          </div>
          <div className={clubStyles.tileRow}>
            {isFieldVisible(module, 'isShaftLengthFinished', pageReadability) && <Tile label="Measured" value={wedge.isShaftLengthFinished ? 'Finished' : 'Cut'} variant="large" />}
            {isFieldVisible(module, 'shaftLengthDigit', pageReadability) && <Tile label="Length" value={`${wedge.shaftLengthDigit} ${wedge.shaftLengthFraction}`} />}
            {isFieldVisible(module, 'shaftCosmetic', pageReadability) && <Tile label="Cosmetic" value={normalizeString(wedge.shaftCosmetic)} />}
          </div>
        </>
      )}
      {isFieldVisible(module, 'gripType', pageReadability) && (
        <>
          <div className={clubStyles.category}>
            <div className={clubStyles.categoryName}>Grip</div>
            <div className={clubStyles.categoryTitle}>{normalizeString(wedge.gripType)}</div>
          </div>
          <div className={clubStyles.tileRow}>
            {isFieldVisible(module, 'gripSize', pageReadability) && <Tile label="Grip Size" value={normalizeString(wedge.gripSize)} variant="large" />}
            {isFieldVisible(module, 'rndRib', pageReadability) && <Tile label="Round/Rib" value={normalizeString(wedge.rndRib)} />}
            {isFieldVisible(module, 'logo', pageReadability) && <Tile label="Logo" value={normalizeString(wedge.logo)} />}
            {isFieldVisible(module, 'tape', pageReadability) && <Tile label="Grip Tape" value={normalizeString(wedge.tape)} variant="large" />}
          </div>
        </>
      )}
      {isFieldVisible(module, 'notes', pageReadability) && (
        <>
          <div className={clubStyles.category}>
            <div className={clubStyles.categoryName}>Notes</div>
          </div>
          <div className={clubStyles.tileRow}>
            <div className={clubStyles.notes}>
              <Linkify options={{ target: '_blank' }}>{normalizeString(wedge.notes)}</Linkify>
            </div>
          </div>
        </>
      )}
      <WedgePhotoCaptureSection playerId={wedge.playerId} ownerRowGuid={wedge.rowGuid} headTypeId={wedge.headTypeId} imageMetadata={wedge.imageMetadata} />
    </>
  );
};

interface IWedgeReadOnlyProps {
  wedgeClubs: TourBagDetail[];
  currentAccordionId: number;
  showCheckbox: boolean;
  /* eslint-disable no-unused-vars */
  isClubSelected: (id: number) => boolean;
  handleOpenAccordion: (id: number) => void;
  handleCheckboxEvent: (id: number, isChecked: boolean) => void;
  pageReadability?: PageReadability | undefined;
  /* eslint-enable no-unused-vars */
}

export const WedgeReadOnly = ({ wedgeClubs, currentAccordionId, showCheckbox, isClubSelected, handleOpenAccordion, handleCheckboxEvent, pageReadability }: IWedgeReadOnlyProps) => {
  return (
    <>
      {wedgeClubs
        ?.sort((a, b) => a.headModelName.localeCompare(b.headModelName))
        ?.sort((a, b) => Number(b.isActive) - Number(a.isActive))
        .map((wedge) => {
          return (
            <ControlledAccordion
              key={wedge.tourBagDetailId}
              id={wedge.tourBagDetailId}
              header={
                <>
                  <span className={clubStyles.model}>
                    {pageReadability === 'limitedView' && wedge.headModelAlternateName ? wedge.headModelAlternateName : `${wedge.headModelName} (${wedge.familyName})`}
                  </span>
                  <InTheBag isActive={wedge.isActive} className={clubStyles.inTheBag} />
                </>
              }
              isOpen={currentAccordionId === wedge.tourBagDetailId}
              showCheckbox={showCheckbox}
              checked={isClubSelected(wedge.tourBagDetailId)}
              handleOpenAccordion={() => handleOpenAccordion(wedge.tourBagDetailId)}
              handleCheckboxEvent={handleCheckboxEvent}
            >
              {renderWedge({ wedge, pageReadability })}
            </ControlledAccordion>
          );
        })}
    </>
  );
};

export default function Wedge() {
  const { tourBag, player, updateTourBag, permissions } = useActivePlayer();
  const tourPageSettings = permissions?.tourPageSettings;
  const { isSyncComplete } = useBackgroundSync();
  const isOnline = useOnlineStatus();
  const wedgeBag = tourBag?.find((bag) => bag.headTypeDescription === 'Wedge');
  const [showDeletedHistory, setShowDeletedHistory] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [clubBag, setClubBag] = useState({} as TourBagMstrPayload);
  const [tourBagDetailList, setTourBagDetailList] = useState([] as TourBagDetail[]);
  const [currentTourBagDetail, setCurrentTourBagDetail] = useState({} as TourBagDetail);
  const { showToast } = useToast();
  const [showSpinner, setShowSpinner] = useState(false);
  const [editPageType, setEditPageType] = useState('');
  const [showFinalHWModal, setShowFinalHWModal] = useState(false);
  const [finalHWIron, setFinalHWIron] = useState<TourBagDetail | null>(null);
  const [showClubHistory, setShowClubHistory] = useState(false);
  const pageReadability = tourPageSettings?.wedge;
  const [showPreEditOfflineModal, setShowPreEditOfflineModal] = useState(false);
  const [showPostEditOfflineModal, setShowPostEditOfflineModal] = useState(false);

  const clubDeletedPermission = permissions?.tourBagSettings?.clubDeleted ?? false;

  useEffect(() => {
    const asyncProcess = async () => {
      let thisClubBag = tourBag?.find((bag) => bag.headTypeDescription.toLowerCase() === 'wedge');
      //if clubBag is empty, then no clubs have been added yet. So create a new one in case the player wants to add a new club to the collection
      if (!thisClubBag) {
        thisClubBag = {
          tourBagMstrId: -1,
          tourId: player?.defaultTourId || 0,
          tourName: player?.defaultTourName || '',
          createDate: new Date(),
          editSource: 'web',
          headTypeDescription: 'Wedge',
          headTypeId: headTypeMap['Wedge'],
          isActive: true,
          isBsg: false,
          isBagEmpty: false,
          modifyDate: new Date(),
          playerId: player?.playerId || 0,
          playerName: player?.fullName || '',
          rowGuid: '',
          notes: '',
          modifyUserGuid: '',
          modifyUserName: '',
          createUserGuid: '',
          tstamp: '',
          tourBagDetail: [] as TourBagDetail[],
          tourBagBsg: [],
          tourBagIronDetail: [],
          tourBagIronSets: [],
        } as TourBagMstrPayload;
        setTourBagDetailList([] as TourBagDetail[]);
      } else {
        setTourBagDetailList(thisClubBag.tourBagDetail);
      }
      setClubBag(thisClubBag);
    };
    asyncProcess();
  }, [tourBag, player?.fullName, player?.playerId, player?.defaultTourId, player?.defaultTourName]);

  const openOfflineEditModal = (tourBagDetailId: number) => {
    let bag = tourBagDetailList?.find((club) => club.tourBagDetailId == tourBagDetailId);
    if (bag) {
      setCurrentTourBagDetail(bag);
      setEditPageType('Edit');
      setShowPreEditOfflineModal(true);
    }
  };

  const handleEdit = (tourBagDetailId: number) => {
    let bag = tourBagDetailList?.find((club) => club.tourBagDetailId == tourBagDetailId);
    if (bag) {
      setCurrentTourBagDetail(bag);
    }
    setEditPageType('Edit');
    setShowEditForm(true);
  };

  const closeEditForm = () => {
    setShowEditForm(false);
  };

  const showEditFormOfflineMode = () => {
    setShowPreEditOfflineModal(false);
    switch (editPageType) {
      case 'Edit':
        handleEdit(currentTourBagDetail.tourBagDetailId);
        break;
      case 'Add':
        openAddForm();
        break;
    }
  };

  const saveEditForm = async (tourBagDetail: TourBagDetail) => {
    try {
      if (clubBag) {
        setShowSpinner(true);
        let thisDriverBag = { ...clubBag };
        const isNewItem = editPageType === 'Add' || editPageType === 'Copy';
        if (isNewItem) {
          thisDriverBag.tourBagDetail = [...thisDriverBag.tourBagDetail, tourBagDetail];
        } else {
          thisDriverBag.tourBagDetail = thisDriverBag.tourBagDetail.map((tb) => {
            return tb.tourBagDetailId == tourBagDetail.tourBagDetailId ? tourBagDetail : tb;
          });
        }
        const action: Action = isNewItem ? 'CREATE' : 'UPDATE';
        await updateTourBag(thisDriverBag, action, tourBagDetail.tourBagDetailId);
        setShowEditForm(false);
      }
      if (!isOnline) {
        //offline mode, so only show the modal
        setShowPostEditOfflineModal(true);
      } else {
        //online mode, so show spinner and toast confirmation
        setShowSpinner(false);
        showToast({ message: 'Club Saved', showCheckmark: true, status: 'success' });
      }
    } catch {
      setShowSpinner(false);
    }
  };

  const handleDeleteClub = async (tourBagDetailId: number) => {
    try {
      const bag = tourBagDetailList?.find((club) => club.tourBagDetailId == tourBagDetailId);
      if (bag) {
        const deletedBag = createDeletedTourBag(bag);
        await TourBagService.saveDeletedTourBag(deletedBag);
        if (clubBag) {
          let thisDriverBag = { ...clubBag };
          thisDriverBag.tourBagDetail = thisDriverBag.tourBagDetail.filter((tb) => tb.tourBagDetailId !== tourBagDetailId);
          const action: Action = 'UPDATE';
          await updateTourBag(thisDriverBag, action, tourBagDetailId);
          showToast({ message: 'Club Deleted', showCheckmark: true });
        }
      }
    } catch {
      showToast({ message: 'Failed to Delete Club', status: 'error' });
    }
  };

  const handleCopy = (tourBagDetailId: number) => {
    let bag = tourBagDetailList?.find((club) => club.tourBagDetailId == tourBagDetailId);
    const itemNo = Math.max(...tourBagDetailList.map((tb) => tb.itemNo)) + 1;
    if (bag) {
      let bagCopy = { ...bag, tourBagMstrId: -1, tourBagDetailId: -1, createDate: new Date(), modifyDate: new Date(), rowGuid: '', itemNo };
      setCurrentTourBagDetail(bagCopy);
      setEditPageType('Copy');
      setShowEditForm(true);
      showToast({ message: 'Club Copied', showCheckmark: true, status: 'success' });
    }
  };

  //user is offline, so show modal confirmation before adding record
  const handleOfflineAddNew = () => {
    setEditPageType('Add');
    setShowPreEditOfflineModal(true);
  };

  const handleClosePostEditOfflineModal = () => {
    setShowPostEditOfflineModal(false);
    setShowSpinner(false);
  };

  const openAddForm = () => {
    //determine itemNo as max of other clubs
    let itemNo = 1;
    if (clubBag.tourBagDetail.length > 0) {
      itemNo = Math.max(...clubBag.tourBagDetail.map((tb) => tb.itemNo)) + 1;
    }

    let bag = new TourBagDetailAddNew();
    bag = {
      ...bag,
      headTypeId: headTypeMap['Wedge'],
      headTypeDescription: 'Wedge',
      playerId: clubBag?.playerId || 0,
      playerName: clubBag?.playerName || '',
      tourId: player?.defaultTourId || 0,
      tourName: player?.defaultTourName || '',
      itemNo: itemNo,
      tourBagMstrId: clubBag.tourBagMstrId || bag.tourBagMstrId,
    };
    setEditPageType('Add');
    setCurrentTourBagDetail(bag);
    setShowEditForm(true);
  };

  const handleShowDeletedHistory = () => {
    setShowDeletedHistory(!showDeletedHistory);
  };

  const handleHistory = (tourBagDetailId: number) => {
    let bag = tourBagDetailList?.find((club) => club.tourBagDetailId == tourBagDetailId);
    if (bag) {
      setCurrentTourBagDetail(bag);
    }
    setShowClubHistory(true);
  };

  const closeClubHistoryModal = () => {
    setShowClubHistory(false);
  };

  return (
    <>
      {showEditForm && <Edit wedge={currentTourBagDetail} closeEditForm={closeEditForm} saveEditForm={saveEditForm} pageType={editPageType} />}

      {!showEditForm && (
        <div className={clubStyles.container}>
          {wedgeBag &&
            wedgeBag?.tourBagDetail.length > 0 &&
            wedgeBag.tourBagDetail
              .sort((a, b) => (a.loftDigit + a.loftFraction).localeCompare(b.loftDigit + b.loftFraction))
              .sort((a, b) => Number(b.isActive) - Number(a.isActive))
              .map((wedge) => {
                return (
                  <Fragment key={wedge.tourBagDetailId}>
                    <Accordion
                      index={wedge.tourBagDetailId}
                      header={
                        <>
                          <span className={clubStyles.model}>
                            {pageReadability === 'limitedView' && wedge.headModelAlternateName ? wedge.headModelAlternateName : `${wedge.headModelName} (${wedge.familyName})`}
                          </span>
                          <InTheBag isActive={wedge.isActive} className={clubStyles.inTheBag} />
                        </>
                      }
                      showMenu={tourPageSettings?.wedge === 'full' && isSyncComplete}
                      showHistory={permissions?.tourBagSettings?.clubHistory ?? false}
                      handleEdit={!isOnline ? openOfflineEditModal : handleEdit}
                      handleDelete={() => handleDeleteClub?.(wedge.tourBagDetailId)}
                      handleCopy={handleCopy}
                      handleHistory={handleHistory}
                    >
                      {renderWedge({
                        wedge,
                        pageReadability: tourPageSettings?.wedge,
                        onFinalHWClick: (wedge) => {
                          setFinalHWIron(wedge);
                          setShowFinalHWModal(true);
                        },
                      })}
                    </Accordion>
                  </Fragment>
                );
              })}
          {!wedgeBag?.tourBagDetail?.length && <div className={clubStyles.noClubs}>No Wedges in System</div>}
          {tourPageSettings?.wedge === 'full' && isSyncComplete && (
            <div className={clubStyles.addNewWrapper}>
              <button className={`${clubStyles.addNew} ${wedgeStyles.addNew}`} onClick={!isOnline ? handleOfflineAddNew : openAddForm}>
                + Add New
              </button>
              {isOnline && clubDeletedPermission && (
                <button className={clubStyles.addNew} onClick={handleShowDeletedHistory}>
                  Show Deleted
                </button>
              )}
            </div>
          )}
          <p />
          {showDeletedHistory && (
            <DeletedHistory
              headTypeId={headTypeMap['Wedge']}
              playerId={clubBag?.playerId || 0}
              playerName={clubBag?.playerName || ''}
              showDeletedHistory={showDeletedHistory}
              closeDeletedHistory={() => setShowDeletedHistory(false)}
            />
          )}
        </div>
      )}
      {showFinalHWModal && finalHWIron && <FinalHeadWeightModal showFinalHWModal={showFinalHWModal} closeFinalHWModal={() => setShowFinalHWModal(false)} iron={finalHWIron} />}
      {showClubHistory && (
        <ClubHistory
          tourBagMstrId={currentTourBagDetail.tourBagMstrId}
          rowGuid={currentTourBagDetail.rowGuid}
          headTypeId={headTypeMap['Wedge']}
          player={player}
          model={currentTourBagDetail.headModelName}
          closeModal={closeClubHistoryModal}
        />
      )}
      {showSpinner && <SpinnerOverlay />}
      <OfflineModal
        onCancel={() => {
          setShowPreEditOfflineModal(false);
        }}
        onClose={showEditFormOfflineMode}
        isOpen={showPreEditOfflineModal}
        modalType={'IsOffline'}
      />
      <OfflineModal onCancel={() => {}} onClose={handleClosePostEditOfflineModal} isOpen={showPostEditOfflineModal} modalType={'ConfirmSave'} />
    </>
  );
}
